/**
 * Hook d'accès au statut d'abonnement du joueur connecté.
 *
 * - Fetch lazy depuis `/api/billing/me` au mount + refresh manuel
 * - Cache localStorage court (`wt_neo_subscription_cache`) pour éviter
 *   un flash "non abonné" pendant le premier fetch
 * - Expose un helper `hasAccess(requiredTier)` utilisé par PaywallGate
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchMySubscription,
  type CurrentSubscription,
  type BillingTier,
  type SubscriptionStatus,
} from '../lib/billingApi';

const STORAGE_KEY = 'wt_neo_subscription_cache';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

const ACTIVE_STATUSES: SubscriptionStatus[] = ['active', 'trialing', 'past_due'];

interface CacheEntry {
  pseudo: string;
  fetchedAt: number;
  subscription: CurrentSubscription | null;
}

function loadCache(pseudo: string): CurrentSubscription | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const cache: CacheEntry = JSON.parse(raw);
    if (cache.pseudo !== pseudo) return null;
    if (Date.now() - cache.fetchedAt > CACHE_TTL_MS) return null;
    return cache.subscription;
  } catch {
    return null;
  }
}

function saveCache(pseudo: string, subscription: CurrentSubscription | null): void {
  try {
    const entry: CacheEntry = { pseudo, fetchedAt: Date.now(), subscription };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage plein ou désactivé : non bloquant
  }
}

export interface UseSubscriptionResult {
  /** Abonnement courant (ou null si aucun ou pas encore chargé) */
  subscription: CurrentSubscription | null;
  /** Indique que le premier fetch est en cours */
  loading: boolean;
  /** Erreur du dernier fetch (null si OK) */
  error: string | null;
  /** Force un re-fetch immédiat */
  refresh: () => Promise<void>;
  /** Renvoie true si l'utilisateur a accès à ce tier (égal ou supérieur) */
  hasAccess: (requiredTier: BillingTier) => boolean;
  /** True si l'abonnement courant est en période d'essai */
  isTrial: boolean;
  /** Nombre de jours restants avant expiration (null si pas applicable) */
  daysUntilExpiry: number | null;
}

// Hiérarchie : free < classic_plus < competition
// (un abonné competition a tout, un abonné classic_plus a le périmètre détente)
const TIER_RANK: Record<BillingTier, number> = {
  free: 0,
  classic_plus: 1,
  competition: 2,
};

function dayDiff(a: Date, b: Date): number {
  return Math.ceil((a.getTime() - b.getTime()) / (24 * 60 * 60 * 1000));
}

export function useSubscription(pseudo: string, token?: string): UseSubscriptionResult {
  const [subscription, setSubscription] = useState<CurrentSubscription | null>(
    () => (pseudo ? loadCache(pseudo) : null),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const doFetch = useCallback(async () => {
    if (!tokenRef.current || !pseudo) return;
    setLoading(true);
    setError(null);
    try {
      const sub = await fetchMySubscription(tokenRef.current);
      setSubscription(sub);
      saveCache(pseudo, sub);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [pseudo]);

  useEffect(() => {
    void doFetch();
  }, [doFetch]);

  const hasAccess = useCallback(
    (requiredTier: BillingTier): boolean => {
      if (requiredTier === 'free') return true;
      if (!subscription) return false;
      if (!ACTIVE_STATUSES.includes(subscription.status)) return false;
      return TIER_RANK[subscription.tier] >= TIER_RANK[requiredTier];
    },
    [subscription],
  );

  const isTrial = subscription?.status === 'trialing';
  const daysUntilExpiry = subscription
    ? dayDiff(new Date(subscription.currentPeriodEnd), new Date())
    : null;

  return {
    subscription,
    loading,
    error,
    refresh: doFetch,
    hasAccess,
    isTrial,
    daysUntilExpiry,
  };
}
